import axios, { type AxiosError, type AxiosRequestConfig } from 'axios';
import { env } from '../../env';

interface AuthHooks {
  getAccessToken: () => string | null;
  tryRefreshSilently: () => Promise<boolean>;
  onUnauthorized: () => void;
}

let hooks: AuthHooks | null = null;

export function registerAuthHooks(next: AuthHooks): void {
  hooks = next;
}

export const httpClient = axios.create({
  baseURL: env.apiUrl,
  timeout: 15_000,
});

httpClient.interceptors.request.use((config) => {
  const headers = config.headers ?? {};
  headers['X-Request-Id'] = crypto.randomUUID();
  if (hooks !== null) {
    const token = hooks.getAccessToken();
    if (token !== null) headers['Authorization'] = `Bearer ${token}`;
  }
  config.headers = headers;
  return config;
});

interface RetryableConfig extends AxiosRequestConfig {
  _retried?: boolean;
}

httpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetryableConfig | undefined;
    const status = error.response?.status ?? 0;
    if (status === 401 && config !== undefined && config._retried !== true && hooks !== null) {
      config._retried = true;
      const refreshed = await hooks.tryRefreshSilently();
      if (refreshed) {
        const next = hooks.getAccessToken();
        if (next !== null && config.headers !== undefined) {
          config.headers['Authorization'] = `Bearer ${next}`;
        }
        return httpClient.request(config);
      }
      hooks.onUnauthorized();
    }
    return Promise.reject(error);
  },
);
